import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import PageHeader from '@/components/Layout/PageHeader.jsx';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.jsx';
import { EmptyState, SkeletonTable } from '@/components/ui/StateComponents.jsx';
import { Field, Input, Select, Textarea } from '@/components/ui/FormComponents.jsx';
import {
  createMovimiento,
  listCajas,
  listMovimientos,
} from '@/services/tesoreria/cajaMovimientos.js';

const buildToday = () => new Date().toISOString().slice(0, 10);

const initialFilters = {
  fechaDesde: '',
  fechaHasta: '',
  tipo: '',
  cajaId: '',
};

const initialFormState = {
  cajaId: '',
  tipo: '',
  concepto: '',
  importe: '',
  fecha: buildToday(),
  observaciones: '',
  referencia: '',
};

const formatDate = (dateValue) => {
  if (!dateValue) return '-';
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return dateValue;
  return parsed.toLocaleDateString('es-AR');
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
  }).format(Number(value));
};

export default function IngresosEgresosCaja() {
  const [filters, setFilters] = useState(initialFilters);
  const [movimientos, setMovimientos] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formState, setFormState] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const cajaOptions = useMemo(
    () => cajas.map((caja) => ({ label: caja.nombre, value: caja.id })),
    [cajas],
  );

  const loadMovimientos = useCallback(async (nextFilters) => {
    setIsLoading(true);
    setError(null);
    try {
      // TODO: Reemplazar por hook conectado a API real de Tesorería.
      const data = await listMovimientos(nextFilters ?? filters);
      setMovimientos(data);
    } catch (fetchError) {
      setError('No se pudieron cargar los movimientos de caja.');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    listCajas().then(setCajas).catch(() => {
      setCajas([]);
    });
  }, []);

  useEffect(() => {
    loadMovimientos(filters);
  }, [filters, loadMovimientos]);

  const handleFilterChange = (field) => (event) => {
    const value = event?.target?.value ?? event;
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    setFilters(initialFilters);
  };

  const handleOpenModal = () => {
    setFormState({ ...initialFormState, fecha: buildToday() });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (isSaving) return;
    setIsModalOpen(false);
  };

  const handleFormChange = (field) => (event) => {
    const value = event?.target?.value ?? event;
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formState.cajaId) {
      errors.cajaId = 'Seleccioná una caja.';
    }
    if (!formState.tipo) {
      errors.tipo = 'Seleccioná un tipo de movimiento.';
    }
    if (!formState.concepto.trim()) {
      errors.concepto = 'Ingresá un concepto.';
    }
    const importeValue = Number(formState.importe);
    if (!formState.importe || Number.isNaN(importeValue) || importeValue <= 0) {
      errors.importe = 'El importe debe ser mayor a 0.';
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    setIsSaving(true);
    try {
      await createMovimiento({
        ...formState,
        importe: Number(formState.importe),
      });
      setIsModalOpen(false);
      await loadMovimientos(filters);
    } catch (submitError) {
      setFormErrors({ form: 'No se pudo guardar el movimiento. Intentá nuevamente.' });
    } finally {
      setIsSaving(false);
    }
  };

  const breadcrumbs = [
    { label: 'Tesorería', href: '/tesoreria' },
    { label: 'Caja' },
    { label: 'Ingresos y Egresos de Caja' },
  ];

  return (
    <div className="mx-auto w-full max-w-[1400px] px-6 py-6">
      <PageHeader
        title="Ingresos y Egresos de Caja"
        subtitle="Registro de movimientos de caja en modo demo."
        breadcrumbs={breadcrumbs}
        actions={
          <Button variant="primary" onClick={handleOpenModal}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nuevo movimiento
          </Button>
        }
      />

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Field label="Fecha desde">
              <Input
                type="date"
                value={filters.fechaDesde}
                onChange={handleFilterChange('fechaDesde')}
              />
            </Field>
            <Field label="Fecha hasta">
              <Input
                type="date"
                value={filters.fechaHasta}
                onChange={handleFilterChange('fechaHasta')}
              />
            </Field>
            <Field label="Tipo">
              <Select value={filters.tipo} onChange={handleFilterChange('tipo')}>
                <option value="">Todos</option>
                <option value="Ingreso">Ingreso</option>
                <option value="Egreso">Egreso</option>
              </Select>
            </Field>
            <Field label="Caja">
              <Select value={filters.cajaId} onChange={handleFilterChange('cajaId')}>
                <option value="">Todas</option>
                {cajaOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleResetFilters}>
              Limpiar filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Movimientos</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonTable rows={4} columns={7} />
          ) : error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              <p className="font-semibold">{error}</p>
              <Button variant="outline" className="mt-3" onClick={() => loadMovimientos(filters)}>
                Reintentar
              </Button>
            </div>
          ) : movimientos.length === 0 ? (
            <EmptyState
              title="No hay movimientos cargados"
              subtitle="Cuando registres un nuevo movimiento aparecerá en este listado."
              primary={
                <Button variant="primary" onClick={handleOpenModal}>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Nuevo movimiento
                </Button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="text-left text-[rgb(var(--muted))]">
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2">Caja</th>
                    <th className="px-3 py-2">Tipo</th>
                    <th className="px-3 py-2">Concepto</th>
                    <th className="px-3 py-2 text-right">Importe</th>
                    <th className="px-3 py-2">Referencia</th>
                    <th className="px-3 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map((movimiento) => (
                    <tr
                      key={movimiento.id}
                      className="border-t border-[rgba(var(--border),0.6)]"
                    >
                      <td className="px-3 py-3 text-[rgb(var(--fg))]">
                        {formatDate(movimiento.fecha)}
                      </td>
                      <td className="px-3 py-3 text-[rgb(var(--fg))]">
                        {movimiento.cajaNombre}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                            movimiento.tipo === 'Ingreso'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-rose-100 text-rose-700'
                          }`}
                        >
                          {movimiento.tipo}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-[rgb(var(--fg))]">
                        {movimiento.concepto}
                      </td>
                      <td className="px-3 py-3 text-right font-semibold text-[rgb(var(--fg))]">
                        {formatCurrency(movimiento.importe)}
                      </td>
                      <td className="px-3 py-3 text-[rgb(var(--fg))]">
                        {movimiento.referencia || '-'}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-2">
                          <Button variant="ghost" size="sm" disabled>
                            Ver
                          </Button>
                          <Button variant="ghost" size="sm" disabled>
                            Editar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ModalBase isOpen={isModalOpen} onClose={handleCloseModal} labelledBy="nuevo-movimiento-title">
        <form onSubmit={handleSubmit}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2
                id="nuevo-movimiento-title"
                className="text-lg font-semibold text-[rgb(var(--fg))]"
              >
                Nuevo movimiento
              </h2>
              <p className="mt-1 text-sm text-[rgb(var(--muted))]">
                Registrá ingresos o egresos de caja en modo demo.
              </p>
            </div>
            <button
              type="button"
              onClick={handleCloseModal}
              className="rounded-lg border border-transparent px-2 py-1 text-sm text-[rgb(var(--muted))] hover:border-[rgba(var(--border),0.6)]"
            >
              Cerrar
            </button>
          </div>

          {formErrors.form ? (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
              {formErrors.form}
            </div>
          ) : null}

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Caja" error={formErrors.cajaId} required>
              <Select value={formState.cajaId} onChange={handleFormChange('cajaId')}>
                <option value="">Seleccioná una caja</option>
                {cajaOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Tipo" error={formErrors.tipo} required>
              <Select value={formState.tipo} onChange={handleFormChange('tipo')}>
                <option value="">Seleccioná un tipo</option>
                <option value="Ingreso">Ingreso</option>
                <option value="Egreso">Egreso</option>
              </Select>
            </Field>
            <Field label="Concepto" error={formErrors.concepto} required>
              <Input
                value={formState.concepto}
                onChange={handleFormChange('concepto')}
                placeholder="Ej: Pago de caja chica"
              />
            </Field>
            <Field label="Importe" error={formErrors.importe} required>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formState.importe}
                onChange={handleFormChange('importe')}
                placeholder="0,00"
              />
            </Field>
            <Field label="Fecha">
              <Input type="date" value={formState.fecha} onChange={handleFormChange('fecha')} />
            </Field>
            <Field label="Referencia">
              <Input
                value={formState.referencia}
                onChange={handleFormChange('referencia')}
                placeholder="Ej: REC-1234"
              />
            </Field>
            <Field label="Observaciones">
              <Textarea
                value={formState.observaciones}
                onChange={handleFormChange('observaciones')}
                placeholder="Notas adicionales"
              />
            </Field>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" type="button" onClick={handleCloseModal} disabled={isSaving}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={isSaving}>
              {isSaving ? 'Guardando...' : 'Guardar movimiento'}
            </Button>
          </div>
        </form>
      </ModalBase>
    </div>
  );
}
